import { all, AxiosResponse } from 'axios';
import { Interval } from 'react-calendar-timeline/dist/lib/types/main';

import { api } from '../../http';
import {
  BaseFilter,
  PageRequest,
  Task,
  TaskForCalendarAssignment,
  TreeNode
} from '../../types/interfaces';
import { Department } from '../../types/interfaces/Department';
import { EmployeeAssignmentScheduleSetting } from '../../types/interfaces/Schedule';

import { ApiResponse } from './interfaces';

function unwrapApiResponseValue<T>(data: any): T | undefined {
  if (!data) return undefined;
  if (typeof data === 'object' && 'isSuccess' in data) {
    return data.isSuccess ? (data.value as T) : undefined;
  }
  return data as T;
}

export const DepartmentsService = {
  getDepartments(params?: {
    forFunctionalStructure?: boolean;
    all?: boolean;
    withSelectAll?: boolean;
    assetId?: number;
    taskTypeId?: number;
    planStart?: string;
    planFinish?: string;
  }): Promise<AxiosResponse<TreeNode[] | undefined>> {
    const searchParams = new URLSearchParams();

    // Handle all parameters including the new assetId
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    return api
      .get<ApiResponse<TreeNode[]>>('department', { params: searchParams })
      .then((resp) => {
        if (resp.status === 400) {
          return { ...resp, data: undefined, error: resp.data } as any;
        }
        if (resp.data.isSuccess) {
          return { ...resp, data: resp.data.value };
        }
        return { ...resp, data: undefined };
      });
  },

  getDepartmentById(
    id: number
  ): Promise<AxiosResponse<Department | undefined>> {
    return api.get<ApiResponse<Department>>(`department/${id}`).then((resp) => {
      if (resp.data.isSuccess) {
        return { ...resp, data: resp.data.value };
      }
      return { ...resp, data: undefined };
    });
  },

  getDepartmentScheduleNextRun(
    id: number
  ): Promise<AxiosResponse<ApiResponse<string | null>>> {
    return api.get<ApiResponse<string | null>>(`department/${id}/schedule`);
  },

  getDepartmentScheduleSetting(
    id: number
  ): Promise<AxiosResponse<EmployeeAssignmentScheduleSetting | null>> {
    return api
      .get<ApiResponse<EmployeeAssignmentScheduleSetting>>(
        `department/${id}/schedule/setting`
      )
      .then((resp) => {
        const value = resp.data.isSuccess ? resp.data.value : undefined;
        const isConfigured =
          !!value &&
          value.periodValue > 0 &&
          (value.scheduleSettings?.length ?? 0) > 0;
        return { ...resp, data: isConfigured ? value : null };
      });
  },

  updateDepartmentScheduleSetting(
    id: number,
    setting: EmployeeAssignmentScheduleSetting
  ): Promise<AxiosResponse<ApiResponse<EmployeeAssignmentScheduleSetting>>> {
    return api.put<ApiResponse<EmployeeAssignmentScheduleSetting>>(
      `department/${id}/schedule/setting`,
      setting
    );
  },

  updateDepartmentScheduleStatus(
    id: number,
    enabled: boolean
  ): Promise<AxiosResponse<void>> {
    return api.put<void>(`department/${id}/schedule/status`, undefined, {
      params: { enabled }
    });
  },

  createDepartment(
    department: Department
  ): Promise<AxiosResponse<ApiResponse<Department>>> {
    return api.post<ApiResponse<Department>>('department', department);
  },

  updateDepartment(
    id: number,
    department: Department
  ): Promise<AxiosResponse<ApiResponse<Department>>> {
    return api.put<ApiResponse<Department>>(`department/${id}`, department);
  },

  deleteDepartment(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return api.delete<ApiResponse<null>>(`department/${id}`);
  },

  readForFunctionalStructure(): Promise<
    AxiosResponse<ApiResponse<TreeNode[]>>
    > {
    return api.get<ApiResponse<TreeNode[]>>('department', {
      params: { forFunctionalStructure: 'true' }
    });
  },

  getTasksByDepartment(
    id: number,
    date: string
  ): Promise<
    AxiosResponse<ApiResponse<Record<string, TaskForCalendarAssignment[]>>>
  > {
    return api.get<ApiResponse<Record<string, TaskForCalendarAssignment[]>>>(
      `department/${id}/task`,
      { params: { date } }
    );
  },

  getAverageProductivity(
    id: number
  ): Promise<AxiosResponse<number | undefined>> {
    return api
      .get<ApiResponse<number>>(`department/${id}/employee/productivity`)
      .then((resp) => {
        if (resp.data.isSuccess) {
          return { ...resp, data: resp.data.value };
        }
        return { ...resp, data: undefined };
      });
  },

  getIntervalsByDepartment(
    id: number,
    taskTypeId?: string | number,
    persons?: (string | number)[],
  ): Promise<AxiosResponse<any[] | undefined>> {
    const queryParams = new URLSearchParams();
    
    if (taskTypeId) {
      queryParams.append('taskTypeId', String(taskTypeId));
    }
    
    if (persons && persons.length > 0) {
      persons.forEach(personId => {
        queryParams.append('persons', String(personId));
      });
    }
    
    const queryString = queryParams.toString();
    const url = queryString 
      ? `/department/${id}/interval?${queryString}`
      : `/department/${id}/interval`;
    
    return api.get<ApiResponse<Interval[]>>(url).then((resp) => {
      if (resp.data.isSuccess) {
        return { ...resp, data: resp.data.value };
      }
      return { ...resp, data: undefined };
    });
  },

  getUnassignedTasksCountByDepartment(
    departmentId: number,
    date?: string
  ): Promise<AxiosResponse<number>> {
    return api
      .get(`department/${departmentId}/task`, {
        params: { unassignedCount: true, ...(date ? { date } : {}) }
      })
      .then((resp) => ({
        ...resp,
        data: Number(
          unwrapApiResponseValue<number>(resp.data) ?? resp.data ?? 0
        )
      }));
  },

  getUnassignedTasksByDepartment(
    departmentId: number,
    date?: string
  ): Promise<AxiosResponse<Task[]>> {
    return api
      .get(`department/${departmentId}/task`, {
        params: { unassigned: true, ...(date ? { date } : {}) }
      })
      .then((resp) => ({
        ...resp,
        data:
          unwrapApiResponseValue<Task[]>(resp.data) ??
          (resp.data as Task[]) ??
          []
      }));
  },

  getDepartmentAssignmentHistory(
    params: PageRequest & {
      sortBy?: string;
      sortDirection?: string;
      parsedFilters?: BaseFilter[];
      fillers?: string;
    }
  ): Promise<AxiosResponse<any>> {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key]) {
        searchParams.append(key, String(params[key]));
      }
    });

    return api.get<ApiResponse<any>>('departmentAssignmentHistory', {
      params: searchParams
    });
  }
};
